import React, { createContext, useContext, useRef, useState, useCallback } from 'react';
import { getApiToken } from '../services/api';

const AnalysisContext = createContext(null);

export function AnalysisProvider({ children }) {
  const [state, setState] = useState({
    dealId: null,
    dealName: null,
    // 'idle' | 'loading' | 'analyzing' | 'saving' | 'complete' | 'error'
    status: 'idle',
    stage: '',
    result: null,
    error: null,
  });

  const abortRef = useRef(null);

  const startAnalysis = useCallback(async (dealId, dealName, params = {}) => {
    // Cancel any in-flight analysis
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setState({ dealId, dealName: dealName || null, status: 'loading', stage: 'Preparing...', result: null, error: null });

    try {
      const token = getApiToken();
      const response = await fetch(`/api/deals/${dealId}/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(params),
        signal: controller.signal,
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let completed = false;
      let streamError = null;

      const processLines = (text) => {
        buffer += text;
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6).trim();
          if (!payload) continue;
          try {
            const event = JSON.parse(payload);
            if (event.type === 'progress') {
              setState(prev => ({ ...prev, status: event.stage || 'analyzing', stage: event.message }));
            } else if (event.type === 'complete') {
              completed = true;
              setState(prev => ({ ...prev, status: 'complete', stage: '', result: event.data }));
              abortRef.current = null;
            } else if (event.type === 'error') {
              streamError = new Error(event.message);
            }
          } catch (_) {}
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          if (buffer.trim()) processLines('');
          break;
        }
        processLines(decoder.decode(value, { stream: true }));
      }

      if (streamError) throw streamError;
      if (!completed) throw new Error('Analysis did not return a result. Please try again.');
    } catch (err) {
      if (err.name === 'AbortError') return; // intentionally cancelled
      setState(prev => ({ ...prev, status: 'error', stage: '', error: err.message || 'Analysis failed.' }));
    }
  }, []);

  const cancelAnalysis = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setState(prev => ({ ...prev, status: 'idle', stage: '', error: null }));
  }, []);

  const clearResult = useCallback((dealId) => {
    setState(prev => {
      if (dealId && prev.dealId !== dealId) return prev;
      return { dealId: null, dealName: null, status: 'idle', stage: '', result: null, error: null };
    });
  }, []);

  return (
    <AnalysisContext.Provider value={{ state, startAnalysis, cancelAnalysis, clearResult }}>
      {children}
    </AnalysisContext.Provider>
  );
}

export function useAnalysis() {
  return useContext(AnalysisContext);
}
